/*  eslint-disable no-async-promise-executor, no-console, no-unused-vars */

import Vue from 'vue';
import { merge, uniqBy } from 'lodash';
import { DateTime } from 'luxon';
import $http, { genericHttp, getErrorArray } from '../../shared/http';
import router from '../../router/index.js';
import { ROLE_NAMES } from '../../shared/utils';
import Notify from 'quasar/src/plugins/Notify.js';;
import { emitter } from '../../event_bus/index.js';

const TOKEN_KEY = 'auth_token';
const AUTH_ORGANISATION_ID = 'auth_organisation_id';
const AUTH_USER_ID = 'auth_user_id';

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

export function getAuthOrgId() {
  return localStorage.getItem(AUTH_ORGANISATION_ID) ?? '';
}

export function getAuthUserId() {
  return localStorage.getItem(AUTH_USER_ID) ?? '';
}

export function isLoggedIn() {
  return !!getAuthToken() && !!getAuthUserId();
}

export function loginUser({ token, organisation_id, user_id }) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(AUTH_ORGANISATION_ID, organisation_id);
  localStorage.setItem(AUTH_USER_ID, user_id);
}

export const auth = {
  namespaced: true,
  state: () => {
    return {
      loginCredentials: null,
      authUser: null,
      status: '',
      csrfToken: null,
      userRegisterData: {
        email: '',
        password: '',
        login_status: null,
        role_id: null,
      },
      accountCreated: false,
      isLoginRecaptchaEnabled: null,
      recaptchaSiteKey: '6LexBakZAAAAAC7udm55N50TdJseaczdEMNwrCyO',
      recaptchaActive: process.env.NODE_ENV === 'production',
      newMemberID: null,
      orgRegisterData: null,
      isEmailExistForLogin: false,
      isRecoveryCredValid: false,
      verifyRecoveryCodeStatus: false,
      verifyPasswordStatus: false,
      passwordChangeStatus: false,
      activationMessage: '',
      passwordLastChangedAt: null,
      pushedToLogin: false,
      verificationGracePeriod: 30, // 30 days
      userOrganisationsForSelect: null,
      currentOrganisation: null,
      intendedRoute: null,
      impersonationSession: {
        active: false,
        churchId: null,
        churchName: null,
      },
      authFlow: {
        isActive: false,
        state: {
          isFirstTimeLogin: false,
          showGreeting: false,
          isForgotPasswordFlow: false,
          greetingText: '',
        },
      },
      userCountry: null,
      gotedoOAuth2Flow: {
        isActive: false,
        data: {},
      },
    };
  },
  getters: {
    getAuthOrg: (state, getters) =>
      state?.authUser?.organisations?.find((org) => org.id === getAuthOrgId()),
    getLoginFlowFormCredentials: (state) => state.authFlow?.state?.form,
    getRefreshToken: (state) => state.refreshToken,
    getNewMemberID: (state) => state.newMemberID,
    getAuthStatus: (state) => state.status,
    getAuthUser: (state) => state.authUser,
    getAuthProfile: (state, getters) => {
      if (getters.isGlobalUser) {
        return state.authUser;
      }

      return state?.authUser?.profiles?.find(
        (profile) => profile.organisation_id === getters.getAuthOrg?.id
      );
    },
    authUserHasProfile: (state) => !!state?.authUser?.profiles?.length,
    getAuthProfileID: (state, getters) => getters.getAuthProfile?.id,
    getAuthZonalID: (state, getters) => getters.getAuthProfile?.zone?.id,
    getAuthUserID: (state, getters) => () => getters?.getAuthUser?.id ?? getAuthUserId(),

    getAuthRoles: (state, getters) => {
      const userRoles = getters?.getAuthUser?.is_global_user
        ? getters?.getAuthUser?.globalRoles
        : [
            ...(getters?.getAuthUser?.churchRoles ?? []),
            ...(getters?.getAuthUser?.ministryRoles ?? []),
          ];

      return (
        userRoles?.map((role) => ({
          id: role.role_id,
          name: role.role.name,
        })) ?? null
      );
    },
    getAuthRolesNames: (state, getters) => getters?.getAuthRoles?.map((role) => role.name) ?? null,

    getAuthMinistries: (state, getters) => getters.getAuthProfile?.ministries ?? null,
    getAuthOrgMinistries: (state, getters) => getters.getAuthOrg?.ministries ?? null,
    getAuthMinistriesNames: (state, getters) =>
      getters.getAuthMinistries?.map((min) => min.name) ?? null,

    getAuthGroups: (state, getters) => getters?.getAuthProfile?.groups,
    getAuthGroupsNames: (state, getters) =>
      getters.getAuthGroups?.map((group) => group.name) ?? null,

    isZonalLeader: (state, getters) =>
      getters?.getAuthRolesNames?.includes(ROLE_NAMES.ZONAL_LEADER) ?? false,

    isMinistryLeader: (state, getters) =>
      getters?.getAuthRolesNames?.includes(ROLE_NAMES.MINISTRY_LEADER) ?? false,

    isGroupLeader: (state, getters) =>
      getters?.getAuthRolesNames?.includes(ROLE_NAMES.GROUP_LEADER) ?? false,

    isChurchOwner: (state, getters, rootState, rootGetters) =>
      rootGetters['organisations/getOrganisationData']?.church_owner === getters.getAuthUserID(),

    isFinancialBudgetApprover: (state, getters) =>
      getters?.getAuthRolesNames?.includes(ROLE_NAMES.FINANCIAL_BUDGET_APPROVER) ?? false,

    getAuthOrgName: (state, getters) => {
      return getters.getAuthOrg ? getters.getAuthOrg.name : '';
    },
    getAuthOrgID: (state, getters) => {
      return getters.getAuthOrg ? getters.getAuthOrg.id : '';
    },
    getAuthOrgSlug: (state, getters) => {
      return getters.getAuthOrg ? getters.getAuthOrg.slug : '';
    },
    getCrsfToken: (state) => state.csrfToken,
    isCredentialValid: (state) =>
      state?.authFlow.state?.form?.email && state?.authFlow.state?.form?.password,
    getUserRegisterData: (state) => state.userRegisterData,
    isAccountCreated: (state) => state.accountCreated,
    getOrgRegisterData: (state) => state.orgRegisterData,
    getLoginRecaptchaStatus: (state) => state.isLoginRecaptchaEnabled,
    getRecaptchaSiteKey: (state) => state.recaptchaSiteKey,
    isRecaptchaActive: (state) => state.recaptchaActive,
    isAuthOrgApproved: (state, getters) => {
      if (getters.getAuthOrg) return Boolean(getters.getAuthOrg.is_org_approved);
      else return null;
    },
    getOrgCreationDate: (state, getters) => getters?.getAuthOrg?.created_at,
    getNumberOfDaysAfterOrgCreation: (state, getters) => {
      const now = DateTime.now();
      const creationDate = DateTime.fromISO(getters.getOrgCreationDate);
      const diffDuration = now.diff(creationDate, 'days');
      return diffDuration.days;
    },
    getVerificationGracePeriod: (state) => state.verificationGracePeriod,
    isOrgWithinVerificationGracePeriod: (state, getters) =>
      !(getters.getNumberOfDaysAfterOrgCreation > getters.getVerificationGracePeriod),
    gracePeriodRemaining: (state, getters) => {
      return Math.round(
        getters.getVerificationGracePeriod - getters.getNumberOfDaysAfterOrgCreation
      );
    },
    isAuthOrgCOC: (state, getters) => {
      return getters.getAuthOrg?.group?.name?.toLowerCase() === 'church of christ' ?? false;
    },
    isAuthOrgVerified: (state, getters) => {
      if (getters.isGlobalUser) return true;
      // Temporarily ease the organisation's verification within the grace period
      return !!getters.getAuthOrg?.is_verified;
    },
    isAuthOrgNotVerifiedAndWithinGracePeriod: (state, getters) => {
      return getters.isOrgWithinVerificationGracePeriod && !getters.isAuthOrgVerified;
    },

    isGlobalUser: (state, getters) => getters?.getAuthUser?.is_global_user,

    isOrgUser: (state, getters) => getters.isGlobalUser === false,

    isOrgAdmin: (state, getters) => {
      return (
        getters?.getAuthRolesNames?.some((role) => {
          return role === ROLE_NAMES.ORG_ADMIN;
        }) ?? false
      );
    },

    belongsToFinancialMinistry: (_, getters) => {
      return !!getters.getAuthMinistries?.find((authMin) => !!authMin.is_responsible_for_finances);
    },

    getEmailExistForLogin: (state) => state.isEmailExistForLogin,
    getRecoveryCredValid: (state) => state.isRecoveryCredValid,
    getVerifyRecoveryCodeStatus: (state) => state.verifyRecoveryCodeStatus,
    getVerifyPasswordStatus: (state) => state.verifyPasswordStatus,
    getPasswordChangeStatus: (state) => state.passwordChangeStatus,
    getActivationMessage: (state) => state.activationMessage,
    getPasswordLastChangedAt: (state) => state.passwordLastChangedAt,
    getPushedToLogin: (state) => state.pushedToLogin,
    getRememberMe: () => () => JSON.parse(localStorage.getItem('gotedo-remember-me')),
    userOrganisationsForSelect: (state) => state.userOrganisationsForSelect,
    getIntendedRoute: (state) => state.intendedRoute,
    GET_IMPERSONATION_SESSION: (state) => state.impersonationSession,
    inStoreResolvedOrgId: (state, getter) => () => {
      return getter.isGlobalUser && getter.GET_IMPERSONATION_SESSION.active
        ? getter.GET_IMPERSONATION_SESSION.churchId
        : getter.getAuthOrg?.id ?? getAuthOrgId();
    },
    getAuthFlowState: (state) => state.authFlow.state,
    isLoginFlowActive: (state) => state.authFlow.isActive,
    isPasswordRecoveryFlowActive: (state) => state.authFlow.state.isForgotPasswordFlow,
    GET_USER_COUNTRY: (state) => state.userCountry,
    getAuthOrgCountry: (state, getters) => {
      return getters?.getAuthOrg?.orgCountry ?? null;
    },
  },
  mutations: {
    setNewMemberID: (state, payload) => (state.newMemberID = payload),
    authRequest: (state) => (state.status = 'loading'),
    authError: (state) => (state.status = 'error'),
    setLoginCredentials: (state, payload) => {
      state.loginCredentials = payload;
    },

    logoutUser: (state, options = {}) => {
      const noPushToLoginPage = !!options.noPushToLoginPage;
      state.status = '';
      state.loginCredentials = {};
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(AUTH_ORGANISATION_ID);
      if (noPushToLoginPage) {
        return;
      }
      emitter.emit('push_to_login_page');
    },
    setAuthUser: (state, user) => {
      state.authUser = user;
    },

    setCsrfToken: (state, token) => {
      state.csrfToken = token;
    },
    setUserRegisterData: (state, formData) => {
      state.userRegisterData.email = formData.email;
      state.userRegisterData.email = formData.email;
      state.userRegisterData.password = formData.password;
      state.userRegisterData.login_status = formData.loginStatus;
      state.userRegisterData.role_id = formData.role;
    },
    initUserData: (state) => {
      state.userRegisterData.email = '';
      state.userRegisterData.email = '';
      state.userRegisterData.password = '';
      state.userRegisterData.login_status = null;
      state.userRegisterData.role_id = null;
    },
    setLoginRecaptchaStatus: (state, status) => {
      state.isLoginRecaptchaEnabled = status;
    },
    setRecaptchaSiteKey: (state, key) => {
      state.recaptchaSiteKey = key;
    },
    setIsAccountCreated: (state, payload) => (state.accountCreated = payload),
    setOrgRegisterData: (state, payload) => (state.orgRegisterData = payload),
    setEmailExistForLogin: (state, payload) => (state.isEmailExistForLogin = payload),
    setRecoveryCredValid: (state, payload) => (state.isRecoveryCredValid = payload),
    setVerifyRecoveryCodeStatus: (state, payload) => (state.verifyRecoveryCodeStatus = payload),
    setVerifyPasswordStatus: (state, payload) => (state.verifyPasswordStatus = payload),
    setPasswordChangeStatus: (state, payload) => (state.passwordChangeStatus = payload),
    setActivationMessage: (state, payload) => (state.activationMessage = payload),
    setPushedToLogin: (state, payload) => (state.pushedToLogin = payload),
    setPasswordLastChangedAt: (state, payload) =>
      (state.passwordLastChangedAt = payload
        ? DateTime.fromISO(payload).toFormat('yyyy-LL-dd HH:mm:ss')
        : null),
    setRememberMe: (_state, payload) => {
      localStorage.setItem('gotedo-remember-me', JSON.stringify(payload));
    },
    deleteRememberMe: (_state) => {
      localStorage.removeItem('gotedo-remember-me');
    },
    setUserOrganisationsForSelect: (state, payload) => (state.userOrganisationsForSelect = payload),
    setCurrentOrganisation: (state, payload) => (state.currentOrganisation = payload),
    setIntendedRoute: (state, payload = null) => {
      state.intendedRoute = payload;
    },
    setImpersonationSession: (state, { active, church }) => {
      if (active) {
        state.impersonationSession.active = true;
        state.impersonationSession.churchId = church.id;
        state.impersonationSession.churchName = church.name;
      } else {
        state.impersonationSession.active = false;
        state.impersonationSession.churchId = null;
        state.impersonationSession.churchName = null;
      }
    },
    setLoginFlowState: (state, payload) => {
      Vue.set(state.authFlow, 'state', merge({ ...state.authFlow.state }, payload));
    },
    setLoginFlowActive: (state, payload) => {
      state.authFlow.isActive = payload;
    },
    resetLoginFlow: (state, payload) => {
      state.authFlow.isActive = false;
      Vue.set(state.authFlow, 'state', {
        isFirstTimeLogin: false,
        showGreeting: false,
        isForgotPasswordFlow: false,
        greetingText: '',
        oauthInProgress: false,
        form: {},
      });
    },
    setUserCountry: (state, payload) => {
      state.userCountry = payload;
    },
    setGotedoOAuth2Flow: (state, payload) => {
      if (!payload) {
        state.gotedoOAuth2Flow.isActive = false;
        state.gotedoOAuth2Flow.data = {};
      } else {
        state.gotedoOAuth2Flow.isActive = true;
        Vue.set(state.gotedoOAuth2Flow, 'data', merge(state.gotedoOAuth2Flow.data, payload));
      }
    },
    resetGotedoOAuth2Responses: (state) => {
      state.gotedoOAuth2Flow.isActive = false;
      Vue.set(state.gotedoOAuth2Flow.data, 'authorizationPayload', undefined);
      Vue.set(state.gotedoOAuth2Flow.data, 'tokenResponse', undefined);
      Vue.set(state.gotedoOAuth2Flow.data, 'authorizationResponse', undefined);
    },
  },
  actions: {
    async EMAIL_PHONE_PASSWORD_LOGIN_ACTION({ commit, dispatch, getters }, payload) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(AUTH_ORGANISATION_ID);

      const {
        token,
        email,
        phone_number,
        password,
        loginCode,
        rememberMe,
        rememberMeToken,
        step,
        organisationId,
      } = payload;

      if (!step || ['start', 'verify', 'finish'].includes(step) === false) {
        Notify.create({
          message: 'Valid login step is required',
          type: 'negative',
          timeout: 0,
        });
        return;
      }

      if ((!email && !phone_number) || !password) {
        Notify.create({
          message: 'Login credentials are not valid',
          type: 'negative',
          timeout: 0,
        });
        return;
      }

      const url = () => {
        if (step === 'start') {
          return 'auth/login/start';
        }
        if (step === 'verify') {
          return 'auth/login/verify/otp';
        }
        return 'auth/login';
      };

      return $http('v2')
        .post(
          url(),
          {
            email,
            phone_number,
            password,
            login_code: loginCode?.toString?.(),
            remember_me_token: rememberMeToken,
            recaptcha_token: token,
            trust_device: rememberMe === true,
            organisation_id: organisationId,
          },
          { isFromAuthForm: true }
        )
        .then(async (response) => {
          return onLoginResponse({ response, commit, dispatch, getters });
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },

    /**
     * Verify the OAuth token for Oauth Providers
     * @param {object} ctx
     * @param {Function} ctx.commit
     * @param {Function} ctx.dispatch
     *
     * @param {object} payload
     * @param {String} payload.token
     * @param {boolean} payload.provider
     *
     * @return {Promise} response || error
     */
    async OAUTH_LOGIN_ACTION({ dispatch, commit, getters }, payload) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(AUTH_ORGANISATION_ID);

      const {
        oAuthToken,
        provider,
        rememberMe,
        rememberMeToken,
        step,
        organisationId,
        email,
        loginCode,
        recaptchaToken,
      } = payload;

      if (!step || ['start', 'verify', 'finish'].includes(step) === false) {
        Notify.create({
          message: 'Valid login step is required',
          type: 'negative',
          timeout: 0,
        });
        return;
      }

      if (['verify', 'finish'].includes(step) && !email) {
        Notify.create({
          message: 'Email address is required',
          type: 'negative',
          timeout: 0,
        });
        return;
      }

      if (!oAuthToken || !provider) {
        Notify.create({
          message: 'Login credentials are not valid',
          type: 'negative',
          timeout: 0,
        });
        return;
      }

      const url = () => {
        if (step === 'start') {
          return `/auth/login/oauth/${provider}/start`;
        }
        if (step === 'verify') {
          return `/auth/login/oauth/${provider}/verify/otp`;
        }
        return `/auth/login/oauth/${provider}`;
      };

      return $http('v2')
        .post(
          url(),
          {
            oauth_token: oAuthToken,
            remember_me_token: rememberMeToken,
            recaptcha_token: recaptchaToken,
            trust_device: rememberMe === true,
            organisation_id: organisationId,
            login_code: loginCode?.toString?.(),
            email,
          },
          { isFromAuthForm: true }
        )
        .then(async (response) => {
          return onLoginResponse({ response, commit, dispatch, getters });
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },

    async logoutUser(
      { commit },
      { message, dontNotify, noLogoutRequest, noPushToLoginPage, messageVariant = 'negative' }
    ) {
      if (!dontNotify) {
        Notify.create({ message, type: messageVariant, timeout: 0 });
      }

      if (noLogoutRequest) {
        commit('logoutUser', { noPushToLoginPage });
        return;
      }

      return (
        // Logout request
        $http('v2')
          // Don't assign `dontNotify` to `notifiable` property
          .delete(`auth/login`, { redirectToLogin: false, notifiable: false })
          .finally(() => {
            // This should run when the logout request succeeds or not.
            commit('logoutUser', { noPushToLoginPage });
          })
      );
    },

    async registerOrgPublic({ getters }) {
      return $http('v2').post(
        'auth/church_registration',
        { ...getters['getOrgRegisterData'] },
        { notifiable: true }
      );
    },

    async getLoginRecaptchaSetting({ dispatch, commit }) {
      return await $http('v2')
        .get('/system_settings/login_recaptcha_setting', {
          isFromAuthForm: true,
        })
        .then((response) => {
          commit('setLoginRecaptchaStatus', response.data.data);
          return response;
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },

    async getRecaptchaSiteKeyAction({ dispatch, commit }) {
      return $http('v2')
        .get('/system_settings/recaptcha_site_key', { isFromAuthForm: true })
        .then((response) => {
          commit('setRecaptchaSiteKey', response.data.data);
          return response;
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },

    async loadAuthProfile({ commit, dispatch }, { churchId, isGlobalUser }) {
      if (churchId === 'undefined') {
        churchId = undefined;
      }

      /**
       * This endpoint will most definitely return a 404 error if user loses
       * access to the Gotedo Vineyard software either because the church got deleted
       * or their account got restricted.
       */
      return $http('v2', { logoutOn404: true })
        .get(churchId && !isGlobalUser ? `organisations/${churchId}/auth_profile` : 'auth_profile')
        .then((response) => {
          commit('setAuthUser', response.data.data);
          dispatch('preferences/setUserPreferences', response.data.data.preferences, {
            root: true,
          });
          return response;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },

    async isUserEmailUnique(ctx, email) {
      return new Promise(async (resolve, reject) => {
        await $http('v2')
          .post('auth/email_uniqueness_check', {
            email,
          })
          .then((response) => {
            resolve(response.data.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    /**
     *
     * @param {Store} context Store Context object
     * @param {Object} payload Payload object
     * @param {function} context.commit Store commit function
     * @param {function} context.dispatch Store dispatch function
     * @param {string} payload.email Login email address
     * @param {boolean} payload.recoveryMode Is request sent from recovery page or not?
     */
    async isEmailExistForLoginAction({ commit }, { email, recoveryMode, recaptchaToken }) {
      commit('setEmailExistForLogin', false);

      return $http('v2')
        .post(
          '/auth/isUserEmailExist',
          {
            email,
            recaptcha_token: recaptchaToken,
          },
          { params: { recoveryMode }, isFromAuthForm: true }
        )
        .then((response) => {
          commit('setEmailExistForLogin', response.data.data.exists);
          return response.data.data;
        });
    },
    async sendRecoveryCodeAction({ commit }, { email }) {
      return $http('v2')
        .post('/auth/recoverAccountPassword', { email }, { isFromAuthForm: true, notifiable: true })
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },
    /**
     * Send new email and iniitiate the email update process
     * @param {Object} ctx Vuex store context object
     * @param {Function} ctx.dispatch
     * @param {Object} payload Payload for POST
     * @param {string} payload.new_email New email for email update
     * @returns {Promise}
     */
    async sendEmailChangeCodeAction(ctx, payload) {
      return $http('v2')
        .post('auth/email_change/initiate', payload, { notifiable: true })
        .then((response) => {
          return response.data.data;
        });
    },
    /**
     * Submits the email change verification code and token and updates
     * the email if code is valid.
     * @param {Object} ctx Vuex store context object
     * @param {Function} ctx.dispatch
     * @param {Object} payload Payload for POST
     * @param {number} payload.verification_code Verification code which was sent to user's email for identity verification
     * @param {string} payload.token A token (UUID) send from server to frontend after verification code was sent.
     * @returns {Promise}
     */
    async verifyEmailChangeCodeAction(ctx, payload) {
      return new Promise(async (resolve, reject) => {
        await $http('v2')
          .post('auth/email_change/update_email', payload, {
            headers: { 'X-Is-Notifiable': 'On' },
            notifiable: true,
          })
          .then((response) => {
            return resolve(response.data.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    /**
     * Send new and old paswords and iniitiate the pasword update process
     * @param {Object} ctx Vuex store context object
     * @param {Function} ctx.dispatch
     * @param {Object} payload Payload for POST
     * @param {string} payload.new_password New password for password update
     * @param {string} payload.old_password Old password for password update
     * @returns {Promise}
     */
    async submitPasswordChangeAction(ctx, payload) {
      return $http('v2')
        .post('auth/password_change/change_password', payload, { notifiable: true })
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },
    async initiatePasswordChangeAction(ctx) {
      return $http('v2').post(
        'auth/password_change/initiate',
        { method: 'email' },
        { notifiable: true }
      );
    },
    /**
     * Submits the password change verification code and token and updates
     * the password if code is valid.
     * @param {Object} ctx Vuex store context object
     * @param {Function} ctx.dispatch
     * @param {Object} payload Payload for POST
     * @param {number} payload.verification_code Verification code which was sent to user's email for identity verification
     * @param {string} payload.token A token (UUID) send from server to frontend after verification code was sent.
     * @returns {Promise}
     */
    async verifyPasswordChangeCodeAction(ctx, payload) {
      return $http('v2')
        .post('auth/password_change/verify', payload, {
          notifiable: true,
        })
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },
    async verifyRecoveryCodeAction({ commit }, form) {
      return $http('v2')
        .post('auth/verifyRecoveryCode', form, { isFromAuthForm: true, notifiable: true })
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },

    async verifyPasswordAction({ commit }, form) {
      commit('setVerifyPasswordStatus', false);

      return $http('v2')
        .post('auth/verifyPassword', form, { isFromAuthForm: true, notifiable: true })
        .then((response) => {
          commit('setVerifyPasswordStatus', true);
          return Promise.resolve(response.data);
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },

    /**
     * Changes user's password following the verification of the recovery code.
     * Used for account recovery via the ForgotPassword form
     * @param {object} ctx
     * @param {Function} ctx.commit
     * @param {object} form Form object
     * @param {String} form.password New password from user
     * @param {String} form.email The email address of the account being recovered.
     * @param {Number} form.recovery_code The recovery code for identity confirmation.
     *
     * @return {Promise} response || error
     */
    async passwordChangeNLoginAction({ commit, dispatch, getters }, form) {
      commit('setPasswordChangeStatus', false);

      return $http('v2')
        .post('/auth/change-password', form, { isFromAuthForm: true, notifiable: true })
        .then((response) => {
          commit('setPasswordChangeStatus', true);
          loginUser(response.data);
          $http().defaults.headers.common['Authorization'] = response.data.token;
          commit('setCurrentOrganisation', response.data.data.organisation_id);
          dispatch('loadAuthProfile', {
            churchId: response.data.data.organisation_id,
            isGlobalUser: getters['isGlobalUser'],
          });
          return response.data.data;
        })
        .catch((error) => {
          return Promise.reject(error?.data);
        });
    },

    async passwordChangePreLoginAction({ commit, dispatch }, form) {
      commit('setPasswordChangeStatus', false);

      return $http('v2')
        .post('/auth/password-change-pre-login', form, {
          isFromAuthForm: true,
          notifiable: true,
        })
        .then((response) => {
          if (response?.data?.data?.is_global_user && response?.data?.token) {
            loginUser(response.data);
            dispatch('loadAuthProfile');
          } else {
            commit(
              'setUserOrganisationsForSelect',
              response.data.data.map((orgData) => {
                const { text, value } = orgData.meta;
                return { text, value };
              })
            );
          }
          return response?.data?.data;
        })
        .catch((error) => {
          return onLoginError({ error, commit });
        });
    },

    /**
     * Query for time user's password was last changed
     * @param {object} ctx
     * @param {Function} ctx.commit
     *
     * @return {Promise} response || error
     */
    async passwordLastChangedAction({ commit }) {
      return new Promise(async (resolve, reject) => {
        commit('setPasswordLastChangedAt', null);
        await $http()
          .get('/auth/passwordLastChangedAt')
          .then((response) => {
            commit('setPasswordLastChangedAt', response.data.data);
            resolve(response.data.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    /**
     * Send activation credentials from user for
     * verification and account activation
     * @param {object} ctx
     * @param {Function} ctx.commit
     * @param {object} payload
     * @param {string} payload.userId
     * @param {string} payload.activationCode
     * @param {string=} payload.churchId
     * @param {string} payload.signature
     *
     * @return {Promise} response || error
     */
    async verifyActivationCredentials({ commit }, { churchId, activationCode, userId, signature }) {
      commit('setActivationMessage', null);
      return $http('v2')
        .post(
          `auth/verify_account_activation_email/${userId}/${activationCode}${
            churchId ? `/${churchId}` : ''
          }?signature=${signature}`,
          undefined,
          { isFromAuthForm: true }
        )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          commit('setActivationMessage', error?.data?.message ?? error?.data ?? error);
          return Promise.reject(error?.data?.message ?? error.data ?? error);
        });
    },

    /**
     * Send activation credentials from user for
     * verification and account activation
     * @param {object} ctx
     * @param {Function} ctx.commit
     * @param {Function} ctx.dispatch
     * @param {object} ID User ID object
     * @param {String} ID.userId User ID
     * @param {String} ID.churchId Church ID
     * @return {Promise} response || error
     */
    async sendActivationEmailAction(ctx, { userId, churchId }) {
      return $http('v2').post(
        churchId
          ? `organisations/${churchId}/users/${userId}/send_account_activation_email`
          : `users/${userId}/send_account_activation_email'`,
        {},
        {
          notifiable: true,
        }
      );
    },

    /**
     * Copies email address from member's profile to member's account
     * @param {object} ctx
     * @param {Function} ctx.dispatch
     * @param {String} user_id
     * @return {Promise} response || error
     */
    async copyEmailToAccountAction(ctx, { userId, churchId }) {
      return await $http('v2')
        .patch(`organisations/${churchId}/users/${userId}/email_update_by_church`, undefined, {
          notifiable: true,
        })
        .then((response) => {
          return response;
        });
    },

    /**
     * Verify recaptcha token for authenticated users
     * @param {object} ctx
     * @param {Function} ctx.commit
     * @param {Function} ctx.dispatch
     * @param {object} tokenObject
     * @param {String} tokenObject.token
     * @param {boolean} tokenObject.auth_needed
     * @return {Promise} response || error
     */
    async verifyRecaptchaToken(ctx, { token, auth_needed }) {
      return $http(auth_needed ? '' : 'v2').post(
        `/auth/verify-recaptcha-token${auth_needed ? '' : '-na'}`,
        { token },
        { notifiable: true, isFromAuthForm: true }
      );
    },

    async fetchUserCountry({ commit }) {
      return $http()
        .get(process.env.VUE_APP_LOCATION_RESOLVER_URL)
        .then((response) => {
          commit('setUserCountry', response.data);
        });
    },

    async fetchPreLoginMe({ commit }) {
      return genericHttp({
        url: '/v2/auth/login/me',
        method: 'GET',
        // CRITICAL: Don't redirect to page when this call return 401
        // status to avoid infinite redirection
        redirectToLogin: false,
      }).then((response) => {
        commit('setGotedoOAuth2Flow', { preLoginUserData: response.data });
        return response;
      });
    },
  },
};

async function onLoginResponse({ response, commit, dispatch, getters }) {
  if (response?.data?.actions?.includes?.('LOGIN_SUCCESS') && response?.data?.data?.token) {
    Notify.create({
      message: response.data.message,
      type: 'positive',
      timeout: 5000,
    });

    if (response?.data?.data?.rememberMe) {
      commit('setRememberMe', response.data.data.rememberMe);
    }

    // For debugging
    // console.log(response.data.data);
    loginUser(response.data.data);
    await dispatch('loadAuthProfile', {
      churchId: response.data.data.organisation_id,
      isGlobalUser: !response.data.data.organisation_id,
    });
    return response.data;
  }

  if (response?.data?.actions?.includes?.('SHOW_CHURCH_SELECTION')) {
    commit('setUserOrganisationsForSelect', response.data.data.churches);
  }

  if (response?.data?.actions?.includes?.('RESET_REMEMBER_ME_TOKEN')) {
    commit('deleteRememberMe');
  }

  Notify.create({
    message: response.data.message,
    type: 'positive',
    timeout: 0,
  });

  return response.data;
}

function onLoginError({ error, commit }) {
  commit('authError');

  if (error === undefined) {
    Notify.create({
      message: 'Something went wrong. Please make sure you are online',
      type: 'negative',
      timeout: 0,
    });
  } else {
    if (typeof error?.data?.message === 'string') {
      Notify.create({
        message: error.data.message,
        type: 'negative',
        timeout: 0,
      });
    } else if (Array.isArray(error?.data?.messages?.errors)) {
      // Validation errors
      getErrorArray({
        errors: error.data.messages.errors,
        isValidationError: error?.status === 422,
      }).forEach((message) => {
        if (message === false) {
          return;
        }

        Notify.create({
          message,
          type: 'negative',
          timeout: 0,
        });
      });
    } else if (Array.isArray(error?.response?.data?.errors)) {
      // Validation errors
      getErrorArray({
        errors: error.response.data.errors,
        isValidationError: (error?.status ?? error?.response?.status) === 422,
      }).forEach((message) => {
        if (message === false) {
          return;
        }

        Notify.create({
          message,
          type: 'negative',
          timeout: 0,
        });
      });
    } else if (Array.isArray(error?.data?.errors)) {
      // Generic error
      getErrorArray({
        errors: error.data.errors,
        isValidationError: error?.status === 422,
      }).forEach((message) => {
        if (message === false) {
          return;
        }

        Notify.create({
          message,
          type: 'negative',
          timeout: 0,
        });
      });
    } else {
      Notify.create({
        message: error?.message ?? error.data.message,
        type: 'negative',
        timeout: 0,
      });

      if (error.data?.code === 'INVALID_REMEMBER_ME_TOKEN') {
        commit('deleteRememberMe');
      }
    }
  }

  return Promise.reject(error);
}
